import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { usePrototype } from '../../contexts/PrototypeContext';
import { reviewScreens } from '../../data/navigation';
import { seasons } from '../../data/seasons';
import { siteConfig } from '../../data/siteConfig';
import { formatPrice } from '../../utils/currency';
import type { SeasonId } from '../../types/content';

/**
 * PROTOTYPE-ONLY review tool. Lives OUTSIDE #customer-canvas and is never part of the
 * customer site. Campaign settings persist only in this browser for review;
 * they are not saved to Website Forge. Nothing is sent or charged.
 */
export function ReviewTool() {
  const [open, setOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const {
    season,
    setSeason,
    campaignNameDraft,
    setCampaignName,
    showCampaignBanner,
    setShowCampaignBanner,
    showFrameBounds,
    setShowFrameBounds
  } = usePrototype();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const groupTitle = 'text-[12px] font-semibold text-ink';
  return <aside data-prototype-only aria-label="Design prototype review tool" className="fixed inset-x-0 top-0 z-50 border-b border-line bg-canvas px-3 py-0.5 md:inset-x-auto md:bottom-[calc(env(safe-area-inset-bottom,0px)+14px)] md:right-5 md:top-auto md:border-0 md:bg-transparent md:p-0 lg:left-5 lg:right-auto" data-id="element-333" data-mp="rf9v968jtapk">
      <AnimatePresence data-id="element-334" data-mp="wdbm4dc7uebl">
        {open && <motion.div id="review-panel" initial={reduceMotion ? false : {
        opacity: 0,
        y: 8,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 8,
        scale: 0.97
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} style={{
        transformOrigin: 'bottom left'
      }} className="absolute left-3 top-14 mb-2 max-h-[min(72vh,600px)] w-[min(340px,calc(100vw-24px))] overflow-y-auto md:static rounded-md border border-line-strong bg-surface p-5 text-ink shadow-[0_12px_40px_rgba(37,39,37,0.18)]" data-id="element-335" data-mp="wcidf6tp5eqt">
            <p className="font-display text-xl" data-id="element-336" data-mp="afqsfi4g795g">{siteConfig.siteName}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-ink-muted" data-id="element-337" data-mp="egkbny6y3zs0">
              Design prototype for review. Not part of the customer site. No data is sent, nothing is charged and no
              orders are created.
            </p>

            <nav aria-label="Review screens" className="mt-5" data-id="element-338" data-mp="ofjciulgyr4g">
              <h2 className={groupTitle} data-id="element-339" data-mp="1uv2e2nj1qzvm">Screens</h2>
              <ul className="mt-2 grid grid-cols-2 gap-1.5" data-id="element-340" data-mp="1uukcrchj1vgu">
                {reviewScreens.map(s => <li key={s.to} data-id="element-341" data-mp="1zvzyz7vfy80t">
                    <NavLink to={s.to} end className={({
                isActive
              }) => `flex min-h-[44px] items-center rounded-[3px] border px-3 text-[13px] transition-colors duration-150 ease-out ${isActive ? 'border-ink bg-ink text-canvas' : 'border-line hover:border-line-strong'}`} data-id="element-342" data-mp="3kbo6opqxcyf0">
                      {s.label}
                    </NavLink>
                  </li>)}
              </ul>
            </nav>

            <div className="mt-5" data-id="element-343" data-mp="2rn0hwk6yvqwp">
              <label htmlFor="campaign-preset" className={groupTitle} data-id="element-344" data-mp="2ro1u4rzpyjg0">
                Holiday or event
              </label>
              <select id="campaign-preset" value={season} onChange={e => setSeason(e.target.value as SeasonId)} className="mt-2 min-h-[44px] w-full min-w-0 rounded-[3px] border border-line-strong bg-canvas px-3 text-[14px] text-ink" data-id="element-345" data-mp="2mq0fcj9bt6ac">
                {seasons.map(s => <option key={s.id} value={s.id} data-id="element-346" data-mp="xwv4bwdiq9j8">{s.label}</option>)}
              </select>
              <label htmlFor="campaign-name" className={`mt-4 block ${groupTitle}`} data-id="element-347" data-mp="iyaurxu0vvgv">
                Sale name
              </label>
              <input id="campaign-name" type="text" value={campaignNameDraft} onChange={e => setCampaignName(e.target.value)} maxLength={80} aria-describedby="campaign-name-help" className="mt-2 min-h-[44px] w-full rounded-[3px] border border-line-strong bg-canvas px-3 text-[16px] text-ink" data-id="element-348" data-mp="nwksaf37z85n" />
              <p id="campaign-name-help" className="mt-1 text-[12px] leading-relaxed text-ink-muted" data-id="element-349" data-mp="3ndiyp6rdype4">
                Up to 80 characters. Updates the banner, hero and product offer; a blank name uses the selected preset.
              </p>
              <label className="mt-3 flex min-h-[44px] cursor-pointer items-center justify-between gap-4" data-id="element-350" data-mp="38rve5kh9tiuq">
                <span className={groupTitle} data-id="element-351" data-mp="33t2aejrsd67r">Show campaign banner</span>
                <input type="checkbox" checked={showCampaignBanner} onChange={e => setShowCampaignBanner(e.target.checked)} className="h-5 w-5 accent-bronze-deep" data-id="element-352" data-mp="3hs1oixhoa2po" />
              </label>
              <p className="mt-2 text-[12px] leading-relaxed text-ink-muted" data-id="element-353" data-mp="3mqmpiz4r0qf6">
                Saved in this browser for review only, not in Website Forge or a CMS. Presets are selected manually;
                there is no automatic holiday schedule.
              </p>
            </div>

            <label className="mt-5 flex min-h-[44px] cursor-pointer items-center justify-between gap-4" data-id="element-354" data-mp="dee501eaeyr">
              <span className={groupTitle} data-id="element-355" data-mp="e8gqo60qz2kj">Show native frame boundaries</span>
              <input type="checkbox" checked={showFrameBounds} onChange={e => setShowFrameBounds(e.target.checked)} className="h-5 w-5 accent-bronze-deep" data-id="element-356" data-mp="9djlja6l9dcx" />
            </label>

            <div className="mt-4 space-y-2 border-t border-line pt-4 text-[12px] leading-relaxed text-ink-muted" data-id="element-357" data-mp="3c44nt6zasend">
              <p data-id="element-358" data-mp="3gvv4duotvyn8">
                <span className="font-semibold text-ink" data-id="element-359" data-mp="3gweb0f9130ly">Client email, Oct 14:</span>{' '}
                {formatPrice(siteConfig.product.samplePrice)} sale / {formatPrice(siteConfig.product.regularPrice)} regular.
                Free shipping.
              </p>
              <p data-id="element-360" data-mp="3uumgxco1lc99">
                <span className="font-semibold text-ink" data-id="element-361" data-mp="3uw5uuwm87b7v">Pending:</span> manual details, warranty and return terms, support
                email and payment processor. See README.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>

      <button type="button" aria-expanded={open} aria-controls="review-panel" onClick={() => setOpen(v => !v)} className="inline-flex h-11 items-center gap-2 rounded-full bg-ink px-4 text-[13px] font-medium text-canvas shadow-[0_6px_20px_rgba(37,39,37,0.25)] transition-colors duration-150 ease-out hover:bg-bronze-deep" data-id="element-362" data-mp="25feltshz91w5">
        <span aria-hidden="true" className="h-2 w-2 rounded-full bg-champagne" data-id="element-363" data-mp="1o499oawfwowj" />
        Design prototype
        {open ? <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-364" data-mp="2gvsz4cjmqz6z" /> : <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-365" data-mp="2gsx1yqflwejl" />}
      </button>
    </aside>;
}